<template>
  <div class="team-deck" :class="{ dark: isDark, mobile: isMobile }">
    <div class="team-deck__header">
      <div>
        <div class="team-deck__title">
          {{ currentTeam ? currentTeam.armyName || '当前队伍' : '当前未在队伍中' }}
        </div>
        <div class="team-deck__meta" v-if="currentTeam">
          <span>{{ currentTeamMemberCount }}/{{ currentTeamMaxCount }}人</span>
          <span>{{ isLeader ? '你是队长' : '你是队员' }}</span>
          <span>最低等级 {{ currentTeam.armyLevel || 0 }}</span>
        </div>
        <div class="team-deck__meta" v-else>
          <span>可以创建队伍、查看公开队伍、接受邀请、邀请推荐玩家以及招募留影</span>
        </div>
      </div>
      <div class="team-deck__header-actions">
        <Badge :count="inviteActiveCount" :offset="[6, 0]">
          <Button size="small" @click="switchView('invites')">邀请</Button>
        </Badge>
        <Button size="small" icon="ios-refresh" :loading="loading" @click="refreshAll">刷新</Button>
      </div>
    </div>

    <div class="team-deck__switcher">
      <button
        v-for="item in navItems"
        :key="item.key"
        type="button"
        class="team-deck__switch"
        :class="{ 'is-active': activeView === item.key }"
        @click="switchView(item.key)"
      >
        <span>{{ item.label }}</span>
        <span v-if="item.count" class="team-deck__switch-count">{{ item.count }}</span>
      </button>
    </div>

    <div class="team-deck__body">
      <div v-if="activeView === 'current'" class="team-deck__panel">
        <div v-if="currentTeam" class="team-card">
          <div class="team-card__header">
            <div>
              <div class="team-card__name">{{ currentTeam.armyName }}</div>
              <div class="team-card__info">
                <span>{{ currentTeamMemberCount }}/{{ currentTeamMaxCount }}人</span>
                <span>最低等级 {{ currentTeam.armyLevel || 0 }}</span>
              </div>
            </div>
            <div class="team-card__ops">
              <Button type="warning" size="small" @click="switchView('recommend')" :disabled="!isLeader">邀请队友</Button>
              <Button type="info" size="small" @click="switchView('snapshot')" :disabled="!isLeader">招募留影</Button>
              <Button type="error" size="small" @click="exitTeam" :loading="submitting">退出</Button>
            </div>
          </div>

          <div class="team-members">
            <div v-for="(item, idx) in currentTeam.charaInfoVoList || []" :key="idx" class="team-member">
              <img v-if="item.avatar" :src="item.avatar" class="team-member__avatar" alt="avatar" />
              <div v-else class="team-member__avatar team-member__avatar--empty">
                <Icon type="ios-person" />
              </div>
              <div class="team-member__content">
                <div class="team-member__name">
                  {{ item.charaName || '未知角色' }}
                  <Tag v-if="Number(item.status) === 1" color="warning" size="small">队长</Tag>
                </div>
                <div class="team-member__meta">
                  <span>{{ item.charaLevel || 0 }}级</span>
                  <span>职业 {{ getOccupationText(item.occupation) }}</span>
                  <span>状态 {{ getTeamMemberStatusText(item.status) }}</span>
                  <span>副本剩余次数 {{ item.rewardNum || 0 }}</span>
                </div>
              </div>
              <div v-if="canKickMember(item)" class="team-member__actions">
                <Button type="error" size="small" ghost :loading="kickSubmitting" @click="kickMember(item)">踢出</Button>
              </div>
            </div>
          </div>

          <div v-if="(currentTeam.snapshotMemberList || []).length" class="snapshot-members">
            <div class="snapshot-members__title">留影成员</div>
            <div class="team-members">
              <div v-for="(item, idx) in currentTeam.snapshotMemberList || []" :key="'snapshot-' + idx" class="team-member">
                <img v-if="item.avatarUrl" :src="item.avatarUrl" class="team-member__avatar" alt="avatar" />
                <div v-else class="team-member__avatar team-member__avatar--empty">
                  <Icon type="ios-person" />
                </div>
                <div class="team-member__content">
                  <div class="team-member__name">
                    {{ item.snapshotName || '未命名留影' }}
                    <Tag color="gold" size="small">留影</Tag>
                  </div>
                  <div class="team-member__meta">
                    <span>所属 {{ formatSnapshotOwner(item) }}</span>
                    <span>{{ item.snapshotLevel || 0 }}级</span>
                    <span>{{ item.snapshotRealm || '凡人' }}</span>
                    <span>招募 {{ formatSnapshotReward(item) }}</span>
                  </div>
                  <div class="team-member__meta">
                    <span>{{ item.rewardDesc || '留影参战不获得副本奖励' }}</span>
                  </div>
                </div>
                <div class="team-member__actions" v-if="isLeader">
                  <Button type="error" size="small" ghost :loading="snapshotSubmitting" @click="removeSnapshotMember(item)">移除</Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="team-empty">
          <Icon type="ios-people-outline" size="32" />
          <p>当前未加入任何队伍</p>
          <div class="team-empty__actions">
            <Button type="primary" size="small" @click="createFormVisible = true">创建队伍</Button>
            <Button size="small" @click="switchView('hall')">查看队伍列表</Button>
          </div>
        </div>
      </div>

      <div v-if="activeView === 'hall'" class="team-deck__panel">
        <div class="team-toolbar">
          <Input v-model="teamKeyword" placeholder="搜索队伍名" clearable style="width: 220px" />
          <Button type="primary" size="small" @click="loadTeamHall" :loading="hallLoading">搜索</Button>
          <Button size="small" @click="createFormVisible = true" :disabled="!!currentTeam">创建队伍</Button>
        </div>
        <div v-if="teamHall.length === 0" class="team-empty">
          <Icon type="ios-search" size="30" />
          <p>暂无可加入的队伍</p>
        </div>
        <div v-else class="team-grid">
          <div v-for="item in teamHall" :key="item.armyId || item.id" class="team-grid__item">
            <div class="team-grid__title">{{ item.armyName || '未命名队伍' }}</div>
            <div class="team-grid__meta">
              <span>{{ Number(item.armyType) === 2 ? '五人队' : '三人队' }}</span>
              <span>队长 {{ resolveHallLeader(item) }}</span>
              <span>最低等级 {{ item.armyLevel || 0 }}</span>
              <span>{{ resolveHallCurrentCount(item) }}/{{ resolveHallMaxCount(item) }}人</span>
            </div>
            <div class="team-grid__actions">
              <Button type="primary" size="small" @click="joinTeam(item)" :disabled="!!currentTeam || hallSubmitting">加入</Button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeView === 'invites'" class="team-deck__panel">
        <div class="team-toolbar">
          <Button icon="ios-refresh" size="small" @click="loadInvites" :loading="inviteLoading">刷新邀请</Button>
        </div>
        <div v-if="inviteList.length === 0" class="team-empty">
          <Icon type="ios-mail-open-outline" size="30" />
          <p>暂无组队邀请</p>
        </div>
        <div v-else class="invite-list">
          <div v-for="item in inviteList" :key="item.inviteId" class="invite-item">
            <div class="invite-item__content">
              <div class="invite-item__title">
                {{ item.armyName || '未知队伍' }}
                <Tag v-if="item.expired" color="default" size="small">已失效</Tag>
                <Tag v-else-if="item.full" color="error" size="small">已满员</Tag>
                <Tag v-else color="success" size="small">有效</Tag>
              </div>
              <div class="invite-item__meta">
                <span>邀请人 {{ item.inviterName || '未知' }}</span>
                <span>{{ item.currentCount || 0 }}/{{ item.maxCount || 0 }}人</span>
                <span>最低等级 {{ item.minLevel || 0 }}</span>
                <span>{{ formatInviteExpire(item) }}</span>
              </div>
            </div>
            <div class="invite-item__actions">
              <Button type="primary" size="small" @click="acceptInvite(item)" :disabled="item.expired || item.full || inviteSubmitting">接受</Button>
              <Button size="small" @click="rejectInvite(item)" :disabled="inviteSubmitting">拒绝</Button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeView === 'recommend'" class="team-deck__panel">
        <div class="team-toolbar">
          <Input v-model="recommendKeyword" placeholder="搜索玩家名" clearable style="width: 220px" />
          <Button type="primary" size="small" @click="loadRecommendations" :loading="recommendLoading">搜索推荐</Button>
        </div>
        <div v-if="recommendList.length === 0" class="team-empty">
          <Icon type="ios-people-outline" size="30" />
          <p>{{ currentTeam ? '暂无可邀请玩家' : '请先创建或加入队伍后再邀请玩家' }}</p>
        </div>
        <div v-else class="recommend-list">
          <div v-for="item in pagedRecommendList" :key="item.charaId" class="recommend-item">
            <div class="recommend-item__main">
              <img v-if="item.avatar" :src="item.avatar" class="recommend-item__avatar" alt="avatar" />
              <div v-else class="recommend-item__avatar recommend-item__avatar--empty">
                <Icon type="ios-person" />
              </div>
              <div class="recommend-item__content">
                <div class="recommend-item__title">{{ item.charaName || item.charaId }}</div>
                <div class="recommend-item__meta">
                  <span>{{ item.level || 0 }}级</span>
                  <span>组队次数 {{ item.rewardNum || 0 }}</span>
                  <span>{{ Number(item.online) === 1 ? '在线' : '离线' }}</span>
                </div>
              </div>
            </div>
            <div class="recommend-item__actions">
              <Button type="warning" size="small" @click="sendInvite(item)" :disabled="!currentTeam || !isLeader || inviteSending">邀请</Button>
            </div>
          </div>
        </div>
        <div v-if="recommendTotalPages > 1" class="recommend-pagination">
          <Button size="small" :disabled="recommendPage <= 1" @click="changeRecommendPage(recommendPage - 1)">上一页</Button>
          <span class="recommend-pagination__text">{{ recommendPage }} / {{ recommendTotalPages }}</span>
          <Button size="small" :disabled="recommendPage >= recommendTotalPages" @click="changeRecommendPage(recommendPage + 1)">下一页</Button>
        </div>
      </div>

      <div v-if="activeView === 'snapshot'" class="team-deck__panel">
        <div class="team-toolbar">
          <Input v-model="snapshotKeyword" placeholder="搜索留影名" clearable style="width: 220px" />
          <Button type="primary" size="small" @click="loadSnapshotHall" :loading="snapshotLoading">搜索</Button>
          <Button size="small" @click="loadMySnapshots" :loading="snapshotLoading">我的留影</Button>
          <Button type="success" size="small" @click="openCreateSnapshot">生成留影</Button>
        </div>
        <div v-if="snapshotHall.length === 0" class="team-empty">
          <Icon type="ios-albums-outline" size="30" />
          <p>暂无可招募留影</p>
        </div>
        <div v-else class="recommend-list snapshot-list">
          <div v-for="item in snapshotHall" :key="item.bountyId || item.snapshotId" class="recommend-item">
            <div class="recommend-item__main">
              <img v-if="item.avatarUrl" :src="item.avatarUrl" class="recommend-item__avatar" alt="avatar" />
              <div v-else class="recommend-item__avatar recommend-item__avatar--empty">
                <Icon type="ios-person" />
              </div>
              <div class="recommend-item__content">
                <div class="recommend-item__title">{{ item.snapshotName || item.snapshotId }}</div>
                <div class="recommend-item__meta">
                  <span>主人 {{ formatSnapshotOwner(item) }}</span>
                  <span>{{ item.snapshotLevel || 0 }}级</span>
                  <span>{{ item.snapshotRealm || '凡人' }}</span>
                  <span>{{ formatSnapshotReward(item) }}</span>
                </div>
                <div class="recommend-item__meta">
                  <span>{{ item.snapshotTitle || '暂无说明' }}</span>
                </div>
              </div>
            </div>
            <div class="recommend-item__actions">
              <Button size="small" @click="openSnapshotDetail(item)">详情</Button>
              <Button
                v-if="isMySnapshot(item)"
                size="small"
                type="primary"
                ghost
                @click="refreshSnapshot(item)"
                :loading="snapshotSubmitting"
              >
                更新
              </Button>
              <Button
                v-if="isMySnapshot(item)"
                size="small"
                type="info"
                @click="openPublishSnapshot(item)"
                :loading="snapshotSubmitting"
              >
                {{ item.bountyId && Number(item.bountyStatus) === 1 ? '重新上架' : '发布' }}
              </Button>
              <Button
                v-if="isMySnapshot(item) && item.canRecall"
                size="small"
                type="error"
                ghost
                @click="recallSnapshot(item)"
                :loading="snapshotSubmitting"
              >
                召回
              </Button>
              <Button
                v-if="!isMySnapshot(item)"
                type="warning"
                size="small"
                @click="recruitSnapshot(item)"
                :disabled="!currentTeam || !isLeader || snapshotSubmitting"
              >
                招募
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      v-model="createFormVisible"
      title="创建队伍"
      width="420"
      :mask-closable="false"
      @on-ok="createTeam"
    >
      <Form :label-width="90">
        <FormItem label="队伍名称">
          <Input v-model="createForm.armyName" maxlength="12" placeholder="请输入队伍名称" />
        </FormItem>
        <FormItem label="最低等级">
          <InputNumber v-model="createForm.armyLevel" :min="1" :max="999" style="width: 100%" />
        </FormItem>
        <FormItem label="队伍类型">
          <RadioGroup v-model="createForm.armyType">
            <Radio :label="1">三人队</Radio>
            <Radio :label="2">五人队</Radio>
          </RadioGroup>
        </FormItem>
      </Form>
    </Modal>

    <Modal v-model="snapshotDetailVisible" title="留影详情" width="640" :footer-hide="true">
      <div v-if="snapshotDetail">
        <div class="team-grid__title">{{ snapshotDetail.snapshotName || '未命名留影' }}</div>
        <div class="team-grid__meta">
          <span>主人 {{ formatSnapshotOwner(snapshotDetail) }}</span>
          <span>{{ snapshotDetail.snapshotLevel || 0 }}级</span>
          <span>{{ snapshotDetail.snapshotRealm || '凡人' }}</span>
          <span>{{ formatSnapshotReward(snapshotDetail) }}</span>
        </div>
        <div class="team-grid__meta" style="margin-top: 12px;">
          <span>{{ snapshotDetail.snapshotTitle || '暂无说明' }}</span>
        </div>

        <div class="snapshot-detail-section">
          <div class="snapshot-detail-title">属性面板</div>
          <div class="snapshot-attr-grid">
            <div v-for="item in snapshotAttrItems" :key="item.label" class="snapshot-attr-card">
              <div class="snapshot-attr-label">{{ item.label }}</div>
              <div class="snapshot-attr-value">{{ item.value }}</div>
            </div>
          </div>
        </div>

        <div class="snapshot-detail-section">
          <div class="snapshot-detail-title">技能列表</div>
          <div v-if="hasSnapshotActiveSkills" class="snapshot-skill-list snapshot-skill-list--slots">
            <div v-for="(skill, idx) in snapshotSkillSlots" :key="skillKey(skill, idx)" class="snapshot-skill-item">
              <div class="snapshot-skill-head">
                <span class="snapshot-skill-name">{{ resolveSkillName(skill) }}</span>
                <!-- <Tag size="small" color="blue">{{ idx + 1 }}号位</Tag> -->
              </div>
              <div class="snapshot-skill-meta">
                <span v-if="resolveSkillRound(skill, idx) > 0">第{{ resolveSkillRound(skill, idx) }}回合</span>
                <span v-if="resolveSkillType(skill)">类型 {{ resolveSkillType(skill) }}</span>
                <!-- <span v-if="resolveSkillRate(skill)">触发 {{ resolveSkillRate(skill) }}</span> -->
              </div>
            </div>
          </div>
          <div v-else class="snapshot-skill-empty">暂无技能数据</div>
          <div v-if="snapshotPassiveSkills.length" class="snapshot-skill-list snapshot-skill-list--passive">
            <div v-for="(skill, idx) in snapshotPassiveSkills" :key="'passive-' + skillKey(skill, idx)" class="snapshot-skill-item">
              <div class="snapshot-skill-head">
                <span class="snapshot-skill-name">{{ resolveSkillName(skill) }}</span>
                <Tag size="small" color="green">被动</Tag>
              </div>
            </div>
          </div>
        </div>
        <div class="snapshot-detail-section">
          <div class="snapshot-detail-title">被招募记录</div>
          <div v-if="snapshotRecruitRecords.length" class="snapshot-record-scroll">
            <div class="snapshot-record-list">
              <div v-for="(record, idx) in snapshotRecruitRecords" :key="record.recordId || idx" class="snapshot-record-item">
                <div class="snapshot-record-head">
                  <span class="snapshot-record-name">{{ record.recruiterName || '未知道友' }}</span>
                  <span class="snapshot-record-reward">+{{ record.rewardCoin || 0 }} 灵石</span>
                </div>
                <div class="snapshot-record-meta">
                  <span>{{ formatSnapshotRecordTime(record.createTime || record.bizDate) }}</span>
                  <span v-if="record.remark">{{ record.remark }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="snapshot-skill-empty">暂无被招募记录</div>
        </div>
      </div>
    </Modal>

    <Modal
      v-model="snapshotFormVisible"
      :title="snapshotForm.snapshotId ? '发布留影' : '生成留影'"
      width="460"
      :mask-closable="false"
      @on-ok="submitSnapshotForm"
    >
      <Form :label-width="96">
        <FormItem label="留影名称">
          <Input v-model="snapshotForm.snapshotName" maxlength="16" placeholder="请输入留影名称（每角色仅1个）" :disabled="!!snapshotForm.snapshotId" />
        </FormItem>
        <FormItem label="招募说明">
          <Input v-model="snapshotForm.snapshotTitle" maxlength="40" placeholder="请输入留影说明" />
        </FormItem>
        <FormItem label="招募灵石">
          <InputNumber v-model="snapshotForm.rewardCoin" :min="50" :max="250" style="width: 100%" />
        </FormItem>
        <FormItem label="每日次数">
          <InputNumber v-model="snapshotForm.dailyLimit" :min="1" :max="10" style="width: 100%" />
        </FormItem>
        <FormItem label="有效小时">
          <InputNumber v-model="snapshotForm.expireHours" :min="24" :max="72" style="width: 100%" />
        </FormItem>
        <FormItem label="规则说明">
          <div class="snapshot-rule-list">
            <p>1. 每角色仅可创建1个留影，生成冷却10分钟。</p>
            <p>2. 发布留影最低20级，招募留影最低10级。</p>
            <p>3. 招募灵石范围50-250，每日最多被招募10次。</p>
            <p>4. 每队最多2个留影，默认有效期72小时。</p>
            <p>5. 留影主人每日通过招募最多获得1000灵石。</p>
          </div>
        </FormItem>
      </Form>
    </Modal>
  </div>
</template>

<script>
export default {
  name: 'TeamDeck',
  props: {
    charaId: {
      type: String,
      default: ''
    },
    isDark: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeView: 'current',
      loading: false,
      hallLoading: false,
      inviteLoading: false,
      recommendLoading: false,
      submitting: false,
      kickSubmitting: false,
      hallSubmitting: false,
      inviteSubmitting: false,
      inviteSending: false,
      snapshotLoading: false,
      snapshotSubmitting: false,
      currentTeam: null,
      currentTeamDetail: null,
      teamHall: [],
      inviteList: [],
      recommendList: [],
      snapshotHall: [],
      mySnapshotList: [],
      snapshotDetail: null,
      snapshotDetailVisible: false,
      snapshotFormVisible: false,
      recommendPage: 1,
      recommendPageSize: 20,
      teamKeyword: '',
      recommendKeyword: '',
      snapshotKeyword: '',
      createFormVisible: false,
      createForm: {
        armyName: '',
        armyLevel: 1,
        armyType: 1
      },
      snapshotForm: {
        snapshotId: '',
        snapshotName: '',
        snapshotTitle: '',
        rewardCoin: 50,
        dailyLimit: 10,
        expireHours: 72
      }
    };
  },
  computed: {
    navItems() {
      return [
        { key: 'current', label: '当前队伍' },
        { key: 'hall', label: '队伍列表' },
        { key: 'invites', label: '邀请列表', count: this.inviteActiveCount || 0 },
        { key: 'recommend', label: '推荐队友' },
        { key: 'snapshot', label: '留影大厅' }
      ];
    },
    currentTeamMaxCount() {
      if (this.currentTeamDetail && this.currentTeamDetail.maxCount) return Number(this.currentTeamDetail.maxCount);
      if (!this.currentTeam) return 0;
      return Number(this.currentTeam.armyType) === 2 ? 5 : 3;
    },
    currentTeamMemberCount() {
      if (this.currentTeamDetail && this.currentTeamDetail.currentCount) return Number(this.currentTeamDetail.currentCount);
      if (!this.currentTeam) return 0;
      const playerCount = Array.isArray(this.currentTeam.charaInfoVoList) ? this.currentTeam.charaInfoVoList.length : 0;
      const snapshotCount = Array.isArray(this.currentTeam.snapshotMemberList) ? this.currentTeam.snapshotMemberList.length : 0;
      return playerCount + snapshotCount;
    },
    isLeader() {
      return !!(this.currentTeamDetail && String(this.currentTeamDetail.leaderCharaId || '') === String(this.getCookie('charaId') || ''));
    },
    inviteActiveCount() {
      return this.inviteList.filter(item => !item.expired).length;
    },
    recommendTotalPages() {
      return Math.max(1, Math.ceil(this.recommendList.length / this.recommendPageSize));
    },
    pagedRecommendList() {
      const start = (this.recommendPage - 1) * this.recommendPageSize;
      return this.recommendList.slice(start, start + this.recommendPageSize);
    },
    snapshotAttrItems() {
      const attr = (this.snapshotDetail && this.snapshotDetail.attrData) || {};
      return [
        { label: '力道攻击', value: attr.physicalAttack || 0 },
        { label: '灵力攻击', value: attr.magicAttack || 0 },
        { label: '力道防御', value: attr.defense || 0 },
        { label: '灵力防御', value: attr.magicDefense || 0 },
        { label: '暴击值', value: attr.criticalHitLevel || 0 },
        { label: '抗暴值', value: attr.criticalResistLevel || 0 },
        { label: '破甲值', value: attr.armorNumber || 0 },
        { label: '暴击率', value: this.formatSnapshotPercent(attr.criticalHitValue) },
        { label: '暴击伤害', value: this.formatSnapshotPercent(attr.critDamage) },
        { label: '力道减伤率', value: this.formatSnapshotPercent(attr.liDaoReductionRate) },
        { label: '灵力减伤率', value: this.formatSnapshotPercent(attr.lingLiReductionRate) },
        { label: '额外伤害', value: this.formatSnapshotPercent(attr.additional) },
        { label: '抗暴率', value: this.formatSnapshotPercent(attr.criticalResist) },
        { label: '破甲率', value: this.formatSnapshotPercent(attr.armor) },
        { label: '连击伤害', value: attr.comboNumber || 0 },
        { label: '反击伤害', value: attr.counterNumber || 0 },
        { label: '持续伤害', value: attr.botNumber || 0 },
        { label: '连击伤害率', value: this.formatSnapshotPercent(attr.combo) },
        { label: '反击伤害率', value: this.formatSnapshotPercent(attr.counter) },
        { label: '持续伤害率', value: this.formatSnapshotPercent(attr.bot) },
        { label: '行动速度', value: attr.movingSpeed || 0 }
      ];
    },
    snapshotSkillSlots() {
      const slots = [null, null, null, null, null];
      this.sortedSnapshotActiveSkills.forEach((skill, idx) => {
        if (!skill) return;
        const order = this.resolveSkillOrder(skill);
        const slotIndex = order >= 1 && order <= 5 ? order - 1 : idx;
        if (slotIndex >= 0 && slotIndex < 5) {
          slots[slotIndex] = skill;
        }
      });
      return slots;
    },
    hasSnapshotActiveSkills() {
      return this.snapshotSkillSlots.some(Boolean);
    },
    sortedSnapshotSkills() {
      const list = (this.snapshotDetail && Array.isArray(this.snapshotDetail.skillList)) ? this.snapshotDetail.skillList.slice() : [];
      return list.sort((a, b) => {
        const roundCompare = this.resolveSkillRound(a, 0) - this.resolveSkillRound(b, 0);
        if (roundCompare !== 0) return roundCompare;
        return this.resolveSkillOrder(a) - this.resolveSkillOrder(b);
      });
    },
    sortedSnapshotActiveSkills() {
      return this.sortedSnapshotSkills.filter(skill => skill && Number(skill.gckSkillType || skill.skillType || 0) !== 2);
    },
    snapshotPassiveSkills() {
      return this.sortedSnapshotSkills.filter(skill => skill && Number(skill.gckSkillType || skill.skillType || 0) === 2);
    },
    snapshotRecruitRecords() {
      const list = this.snapshotDetail && Array.isArray(this.snapshotDetail.recruitRecords)
        ? this.snapshotDetail.recruitRecords
        : [];
      return list.filter(Boolean);
    }
  },
  mounted() {
    // 只加载当前队伍数据，不加载队伍列表（getArmyList）
    // 队伍列表在用户切换到"队伍大厅"视图时才加载
    this.loadCurrentTeam();
  },
  methods: {
    getCookie(name) {
      if (this.charaId) return this.charaId;
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    },
    switchView(name) {
      this.activeView = name;
      if (name === 'hall') this.loadTeamHall();
      if (name === 'invites') this.loadInvites();
      if (name === 'recommend') this.loadRecommendations();
      if (name === 'snapshot') this.loadSnapshotHall();
    },
    changeRecommendPage(page) {
      const nextPage = Math.max(1, Math.min(this.recommendTotalPages, Number(page) || 1));
      this.recommendPage = nextPage;
    },
    isSelfMember(item) {
      const currentCharaId = String(this.getCookie('charaId') || '');
      return !!item && String(item.charaId || '') === currentCharaId;
    },
    isLeaderMember(item) {
      if (!item) return false;
      if (Number(item.status) === 1) return true;
      return String(item.charaId || '') === String((this.currentTeamDetail && this.currentTeamDetail.leaderCharaId) || '');
    },
    getOccupationText(occupation) {
      const mapping = {
        0: '普修',
        1: '力修',
        2: '体修',
        3: '灵修'
      };
      const key = Number(occupation);
      return Object.prototype.hasOwnProperty.call(mapping, key) ? mapping[key] : '普修';
    },
    getTeamMemberStatusText(status) {
      return Number(status) === 1 ? '队长' : '队员';
    },
    canKickMember(item) {
      if (!this.isLeader || !item) return false;
      if (this.isSelfMember(item)) return false;
      if (this.isLeaderMember(item)) return false;
      return !!item.charaNo;
    },
    isMySnapshot(item) {
      return !!item && String(item.ownerCharaId || '') === String(this.getCookie('charaId') || '');
    },
    formatSnapshotOwner(item) {
      if (!item) return '未知主人';
      return item.ownerName || '未知主人';
    },
    formatSnapshotReward(item) {
      if (!item) return '待发布';
      const rewardCoin = Number(item.rewardCoin || 0);
      if (rewardCoin >= 50) {
        return `${rewardCoin} 灵石`;
      }
      return item.bountyId ? '待设置' : '待发布';
    },
    async refreshAll() {
      this.loading = true;
      try {
        await Promise.all([this.loadCurrentTeam(), this.loadTeamHall(), this.loadInvites()]);
        await this.loadSnapshotHall();
        if (this.currentTeam) {
          await this.loadRecommendations();
        } else {
          this.recommendList = [];
        }
      } finally {
        this.loading = false;
      }
    },
    async loadCurrentTeam() {
      try {
        const res = await this.$http.post(`/gameAmry/armyBoundary?charaId=${this.getCookie('charaId')}`);
        this.currentTeam = res.data && res.data.data ? res.data.data : null;
        if (this.currentTeam && this.currentTeam.armyId) {
          try {
            const detailRes = await this.$http.post(`/gameAmry/queryArmyDetail?armyId=${this.currentTeam.armyId}`);
            this.currentTeamDetail = (detailRes.data && detailRes.data.data) || null;
          } catch (detailErr) {
            this.currentTeamDetail = null;
          }
        } else {
          this.currentTeamDetail = null;
        }
      } catch (err) {
        this.currentTeam = null;
        this.currentTeamDetail = null;
      }
    },
    async loadTeamHall() {
      this.hallLoading = true;
      try {
        const res = await this.$http.post(`/gameAmry/getArmyList?armyName=${encodeURIComponent(this.teamKeyword || '')}`);
        this.teamHall = (res.data && res.data.data) || [];
      } catch (err) {
        this.teamHall = [];
        this.$Message.warning((err.data && err.data.msg) || '获取队伍列表失败');
      } finally {
        this.hallLoading = false;
      }
    },
    async loadInvites() {
      this.inviteLoading = true;
      try {
        const res = await this.$http.post(`/gameAmry/queryMyInvites?charaId=${this.getCookie('charaId')}`);
        this.inviteList = (res.data && res.data.data) || [];
      } catch (err) {
        this.inviteList = [];
        this.$Message.warning((err.data && err.data.msg) || '获取邀请列表失败');
      } finally {
        this.inviteLoading = false;
      }
    },
    async loadRecommendations() {
      if (!this.currentTeam) {
        this.recommendList = [];
        this.recommendPage = 1;
        return;
      }
      this.recommendLoading = true;
      try {
        const res = await this.$http.post(`/gameAmry/queryInviteCandidates?charaId=${this.getCookie('charaId')}&keyword=${encodeURIComponent(this.recommendKeyword || '')}`);
        this.recommendList = (res.data && res.data.data) || [];
        this.recommendPage = 1;
      } catch (err) {
        this.recommendList = [];
        this.recommendPage = 1;
        this.$Message.warning((err.data && err.data.msg) || '获取推荐玩家失败');
      } finally {
        this.recommendLoading = false;
      }
    },
    async loadSnapshotHall() {
      this.snapshotLoading = true;
      try {
        const res = await this.$http.get(`/gameArmySnapshot/hall?charaId=${this.getCookie('charaId')}&keyword=${encodeURIComponent(this.snapshotKeyword || '')}`);
        this.snapshotHall = (res.data && res.data.data) || [];
      } catch (err) {
        this.snapshotHall = [];
        this.$Message.warning((err.data && err.data.msg) || '获取留影大厅失败');
      } finally {
        this.snapshotLoading = false;
      }
    },
    async loadMySnapshots() {
      this.snapshotLoading = true;
      try {
        const res = await this.$http.get(`/gameArmySnapshot/mySnapshots?charaId=${this.getCookie('charaId')}`);
        this.mySnapshotList = (res.data && res.data.data) || [];
        this.snapshotHall = this.mySnapshotList;
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '获取我的留影失败');
      } finally {
        this.snapshotLoading = false;
      }
    },
    openCreateSnapshot() {
      this.snapshotForm = {
        snapshotId: '',
        snapshotName: '',
        snapshotTitle: '',
        rewardCoin: 50,
        dailyLimit: 10,
        expireHours: 72
      };
      this.snapshotFormVisible = true;
    },
    openPublishSnapshot(item) {
      this.snapshotForm = {
        snapshotId: item.snapshotId || '',
        snapshotName: item.snapshotName || '',
        snapshotTitle: item.snapshotTitle || '',
        rewardCoin: item.rewardCoin || 50,
        dailyLimit: item.dailyLimit || 10,
        expireHours: 72
      };
      this.snapshotFormVisible = true;
    },
    async submitSnapshotForm() {
      if (!this.snapshotForm.snapshotId && !this.snapshotForm.snapshotName) {
        this.$Message.warning('请输入留影名称');
        return false;
      }
      if ((this.snapshotForm.rewardCoin || 0) < 50 || (this.snapshotForm.rewardCoin || 0) > 250) {
        this.$Message.warning('招募灵石范围为50-250');
        return false;
      }
      if ((this.snapshotForm.dailyLimit || 0) < 1 || (this.snapshotForm.dailyLimit || 0) > 10) {
        this.$Message.warning('每日次数范围为1-10');
        return false;
      }
      this.snapshotForm.expireHours = 72;
      this.snapshotSubmitting = true;
      try {
        let snapshotId = this.snapshotForm.snapshotId;
        if (!snapshotId) {
          const createRes = await this.$http.post(
            `/gameArmySnapshot/create?charaId=${this.getCookie('charaId')}&snapshotName=${encodeURIComponent(this.snapshotForm.snapshotName)}&snapshotTitle=${encodeURIComponent(this.snapshotForm.snapshotTitle || '')}`
          );
          snapshotId = (createRes.data && createRes.data.data) || '';
        }
        if (!snapshotId) {
          this.$Message.warning('留影创建失败');
          return false;
        }
        await this.$http.post(
          `/gameArmySnapshot/publish?charaId=${this.getCookie('charaId')}&snapshotId=${snapshotId}&rewardCoin=${this.snapshotForm.rewardCoin || 50}&dailyLimit=${this.snapshotForm.dailyLimit || 10}&expireHours=${this.snapshotForm.expireHours || 72}`
        );
        this.$Message.success(this.snapshotForm.snapshotId ? '留影发布成功' : '留影生成并发布成功');
        this.snapshotFormVisible = false;
        this.activeView = 'snapshot';
        await this.loadMySnapshots();
        return true;
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '留影操作失败');
        return false;
      } finally {
        this.snapshotSubmitting = false;
      }
    },
    async openSnapshotDetail(item) {
      if (!item || !item.bountyId) return;
      try {
        const res = await this.$http.get(`/gameArmySnapshot/detail?charaId=${this.getCookie('charaId')}&bountyId=${item.bountyId}`);
        const data = (res.data && res.data.data) || null;
        if (data) {
          data.panelData = this.safeParseJson(data.panelData, {});
          data.skillList = this.safeParseJson(data.skillList, []);
          data.attrData = this.safeParseJson(data.attrData, {});
          data.recruitRecords = Array.isArray(data.recruitRecords) ? data.recruitRecords : [];
        }
        this.snapshotDetail = data;
        this.snapshotDetailVisible = true;
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '获取留影详情失败');
      }
    },
    async recruitSnapshot(item) {
      if (!item || !item.bountyId) return;
      this.snapshotSubmitting = true;
      try {
        await this.$http.post(`/gameArmySnapshot/recruit?charaId=${this.getCookie('charaId')}&bountyId=${item.bountyId}`);
        this.$Message.success('招募留影成功');
        await this.refreshAll();
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '招募留影失败');
      } finally {
        this.snapshotSubmitting = false;
      }
    },
    async removeSnapshotMember(item) {
      if (!item || !item.snapshotId || !this.currentTeam || !this.currentTeam.armyId) return;
      this.snapshotSubmitting = true;
      try {
        await this.$http.post(`/gameArmySnapshot/removeMember?charaId=${this.getCookie('charaId')}&armyId=${this.currentTeam.armyId}&snapshotId=${item.snapshotId}`);
        this.$Message.success('移除留影成功');
        await this.refreshAll();
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '移除留影失败');
      } finally {
        this.snapshotSubmitting = false;
      }
    },
    async refreshSnapshot(item) {
      if (!item || !item.snapshotId) return;
      this.snapshotSubmitting = true;
      try {
        await this.$http.post(`/gameArmySnapshot/refresh?charaId=${this.getCookie('charaId')}&snapshotId=${item.snapshotId}&snapshotTitle=${encodeURIComponent(item.snapshotTitle || '')}`);
        this.$Message.success('留影更新成功');
        await this.loadMySnapshots();
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '留影更新失败');
      } finally {
        this.snapshotSubmitting = false;
      }
    },
    async recallSnapshot(item) {
      if (!item || !item.snapshotId) return;
      this.snapshotSubmitting = true;
      try {
        await this.$http.post(`/gameArmySnapshot/recall?charaId=${this.getCookie('charaId')}&snapshotId=${item.snapshotId}`);
        this.$Message.success('留影召回成功');
        await this.loadMySnapshots();
        await this.loadCurrentTeam();
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '留影召回失败');
      } finally {
        this.snapshotSubmitting = false;
      }
    },
    async createTeam() {
      if (!this.createForm.armyName) {
        this.$Message.warning('请输入队伍名称');
        return false;
      }
      this.submitting = true;
      try {
        await this.$http.post(`/gameAmry/playerCreateAmry?charaId=${this.getCookie('charaId')}&armyName=${encodeURIComponent(this.createForm.armyName)}&armyLevel=${this.createForm.armyLevel || 1}&armyType=${this.createForm.armyType || 1}`);
        this.$Message.success('创建队伍成功');
        this.createFormVisible = false;
        this.activeView = 'current';
        await this.refreshAll();
        this.$emit('team-updated');
        return true;
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '创建队伍失败');
        return false;
      } finally {
        this.submitting = false;
      }
    },
    async joinTeam(item) {
      if (!item || !item.armyId) return;
      this.hallSubmitting = true;
      try {
        await this.$http.post(`/gameAmry/playerJoinAmry?charaId=${this.getCookie('charaId')}&amryId=${item.armyId}`);
        this.$Message.success('加入队伍成功');
        this.activeView = 'current';
        await this.refreshAll();
        this.$emit('team-updated');
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '加入队伍失败');
      } finally {
        this.hallSubmitting = false;
      }
    },
    async exitTeam() {
      this.submitting = true;
      try {
        await this.$http.post(`/gameAmry/exitAmry?charaId=${this.getCookie('charaId')}`);
        this.$Message.success('已退出队伍');
        await this.refreshAll();
        this.$emit('team-updated');
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '退出队伍失败');
      } finally {
        this.submitting = false;
      }
    },
    async kickMember(item) {
      if (!this.canKickMember(item)) return;
      this.kickSubmitting = true;
      try {
        const res = await this.$http.post(`/gameAmry/kickOutAmry/?charaId=${this.getCookie('charaId')}&charaNo=${item.charaNo}`);
        this.$Message.success((res.data && res.data.msg) || '已踢出队员');
        await this.refreshAll();
        this.$emit('team-updated');
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '踢出队员失败');
      } finally {
        this.kickSubmitting = false;
      }
    },
    async sendInvite(item) {
      if (!item || !item.charaId) return;
      this.inviteSending = true;
      try {
        await this.$http.post(`/gameAmry/sendArmyInvite?charaId=${this.getCookie('charaId')}&targetCharaId=${item.charaId}`);
        this.$Message.success(`已邀请 ${item.charaName || item.charaId}`);
        await this.loadRecommendations();
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '发送邀请失败');
      } finally {
        this.inviteSending = false;
      }
    },
    async acceptInvite(item) {
      if (!item || !item.inviteId) return;
      this.inviteSubmitting = true;
      try {
        await this.$http.post(`/gameAmry/acceptArmyInvite?charaId=${this.getCookie('charaId')}&inviteId=${item.inviteId}`);
        this.$Message.success('加入队伍成功');
        this.activeView = 'current';
        await this.refreshAll();
        this.$emit('team-updated');
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '接受邀请失败');
      } finally {
        this.inviteSubmitting = false;
      }
    },
    async rejectInvite(item) {
      if (!item || !item.inviteId) return;
      this.inviteSubmitting = true;
      try {
        await this.$http.post(`/gameAmry/rejectArmyInvite?charaId=${this.getCookie('charaId')}&inviteId=${item.inviteId}`);
        this.$Message.success('已拒绝邀请');
        await this.loadInvites();
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '拒绝邀请失败');
      } finally {
        this.inviteSubmitting = false;
      }
    },
    resolveHallMaxCount(item) {
      return Number(item.armyType) === 2 ? 5 : 3;
    },
    resolveHallLeader(item) {
      if (!item) return '未知';
      return item.captainName || item.leaderName || item.charaName || '未知';
    },
    resolveHallCurrentCount(item) {
      const max = this.resolveHallMaxCount(item);
      const remain = Number(item.armyNum || 0);
      return Math.max(1, max - remain);
    },
    formatInviteExpire(item) {
      const expireTime = Number(item.expireTime || 0);
      if (!expireTime) return '无时效';
      const remain = Math.max(0, expireTime - Date.now());
      if (remain <= 0) return '已失效';
      const mins = Math.floor(remain / 60000);
      const secs = Math.floor((remain % 60000) / 1000);
      return `${mins}分${secs}秒后失效`;
    },
    resolveSkillRound(skill, idx) {
      const round = Number(skill && (skill.round || skill.skillRound || skill.gckSkillOrder || skill.order || 0));
      return round > 0 ? round : idx + 1;
    },
    resolveSkillOrder(skill) {
      const order = Number(skill && (skill.gckSkillOrder || skill.order || skill.skillOrder || 0));
      return order > 0 ? order : 0;
    },
    resolveSkillName(skill) {
      if (!skill) return '空';
      return (skill && (
        skill.skillName ||
        skill.name ||
        (skill.gameSkill && (skill.gameSkill.gSkillName || skill.gameSkill.skillName)) ||
        skill.gckSkillId ||
        skill.skillId
      )) || '未知技能';
    },
    resolveSkillType(skill) {
      const type = Number(skill && (skill.gckSkillType || skill.skillType || 0));
      if (type === 1) return '主动';
      if (type === 2) return '被动';
      return '';
    },
    resolveSkillRate(skill) {
      const rate = skill && (skill.gckSkillTriProbability || skill.skillTriProbability || skill.probability);
      if (rate === undefined || rate === null || rate === '') return '';
      const numeric = Number(rate);
      if (Number.isNaN(numeric)) return `${rate}`;
      return numeric <= 1 ? `${Math.round(numeric * 100)}%` : `${numeric}%`;
    },
    skillKey(skill, idx) {
      return `${skill && (skill.gckSkillId || skill.skillId || skill.skillName || 'skill')}-${idx}`;
    },
    formatSnapshotPercent(value) {
      const numeric = Number(value || 0);
      return `${(numeric * 100).toFixed(2)}%`;
    },
    formatSnapshotRecordTime(value) {
      if (!value) return '--';
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return `${value}`;
      const pad = num => `${num}`.padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    safeParseJson(value, fallback) {
      if (!value) return fallback;
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch (e) {
        return fallback;
      }
    }
  }
};
</script>

<style scoped>
.team-deck {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 620px;
}

.team-deck__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.team-deck__title {
  font-size: 14px;
  font-weight: 600;
}

.team-deck__meta {
  display: flex;
  gap: 10px;
  margin-top: 6px;
  color: #657180;
  flex-wrap: wrap;
}

.team-deck__header-actions,
.team-empty__actions,
.team-toolbar,
.team-card__ops,
.invite-item__actions,
.recommend-item__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.team-deck__switcher {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.team-deck__switch {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #515a6e;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.team-deck__switch.is-active {
  border-color: #2d8cf0;
  background: rgba(45, 140, 240, 0.1);
  color: #2d8cf0;
}

.team-deck__switch-count {
  margin-left: 4px;
  font-size: 11px;
}

.team-deck__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: hidden;
}

.team-deck__panel,
.team-grid,
.invite-list,
.recommend-list {
  display: grid;
  gap: 10px;
}

.team-deck__panel {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
  align-content: start;
}

.team-card,
.team-grid__item,
.invite-item,
.recommend-item {
  border: 1px solid #e8eaec;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
}

.team-card__header,
.invite-item,
.recommend-item,
.team-grid__item {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.team-card__info,
.team-grid__meta,
.invite-item__meta,
.recommend-item__meta,
.team-member__meta {
  display: flex;
  gap: 10px;
  margin-top: 6px;
  flex-wrap: wrap;
  color: #657180;
  font-size: 12px;
}

.team-members {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  overflow-y: auto;
  margin-top: 12px;
}

.team-member,
.recommend-item__main {
  display: flex;
  gap: 10px;
  align-items: center;
}

.team-member__content {
  flex: 1;
  min-width: 0;
}

.team-member__actions {
  margin-left: auto;
}

.team-member__avatar,
.recommend-item__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  background: #f4f6f8;
}

.team-member__avatar--empty,
.recommend-item__avatar--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #909399;
}

.team-member__name,
.invite-item__title,
.recommend-item__title,
.team-grid__title,
.team-card__name {
  font-size: 13px;
  font-weight: 600;
}

.team-empty {
  border: 1px dashed #dcdfe6;
  border-radius: 12px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #909399;
}

.team-toolbar {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  margin-bottom: 12px;
  z-index: 2;
  padding-bottom: 4px;
  background: inherit;
}

.team-grid,
.invite-list {
  align-content: start;
  max-height: 230px;
  overflow-y: auto;
}

.recommend-list {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-content: start;
}

.snapshot-list {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.snapshot-list::-webkit-scrollbar {
  width: 6px;
}

.snapshot-list::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.7);
  border-radius: 999px;
}

.snapshot-list::-webkit-scrollbar-track {
  background: rgba(226, 232, 240, 0.65);
  border-radius: 999px;
}

.recommend-item {
  flex-direction: column;
  align-items: stretch;
}

.recommend-item__main {
  align-items: flex-start;
}

.recommend-item__actions {
  justify-content: flex-end;
}

.recommend-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
}

.recommend-pagination__text {
  color: #657180;
}

.snapshot-members {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px dashed #dcdfe6;
}

.snapshot-members__title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
}

.snapshot-rule-list {
  color: #657180;
  font-size: 12px;
  line-height: 1.7;
}

.snapshot-rule-list p {
  margin: 0;
}

.snapshot-detail-section {
  margin-top: 16px;
}

.snapshot-detail-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
}

.snapshot-attr-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.snapshot-attr-card {
  border: 1px solid #e8eaec;
  border-radius: 10px;
  padding: 10px;
  background: #fafbfc;
}

.snapshot-attr-label {
  font-size: 12px;
  color: #8a94a6;
}

.snapshot-attr-value {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
}

.snapshot-skill-list {
  display: grid;
  gap: 10px;
}

.snapshot-skill-list--slots {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.snapshot-skill-item {
  border: 1px solid #e8eaec;
  border-radius: 10px;
  padding: 12px;
  background: #fafbfc;
}

.snapshot-skill-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
}

.snapshot-skill-name {
  font-size: 13px;
  font-weight: 600;
}

.snapshot-skill-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
  color: #657180;
  font-size: 12px;
}

.snapshot-skill-empty {
  color: #909399;
  font-size: 12px;
}

.snapshot-record-scroll {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.snapshot-record-list {
  display: grid;
  gap: 10px;
}

.snapshot-record-item {
  border: 1px solid #e8eaec;
  border-radius: 10px;
  padding: 12px;
  background: #fafbfc;
}

.snapshot-record-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.snapshot-record-name,
.snapshot-record-reward {
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
}

.snapshot-record-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
  color: #657180;
  font-size: 12px;
}

.snapshot-record-scroll::-webkit-scrollbar {
  width: 6px;
}

.snapshot-record-scroll::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.7);
  border-radius: 999px;
}

.snapshot-record-scroll::-webkit-scrollbar-track {
  background: rgba(226, 232, 240, 0.65);
  border-radius: 999px;
}

.dark .team-card,
.dark .team-grid__item,
.dark .invite-item,
.dark .recommend-item,
.dark .team-deck__switch {
  background: #1f2329;
  border-color: #343b45;
  color: #d7deed;
}

.dark .team-deck__switch.is-active {
  border-color: #4975d4;
  background: rgba(73, 117, 212, 0.18);
  color: #8db1ff;
}

.dark .team-deck__meta,
.dark .team-grid__meta,
.dark .invite-item__meta,
.dark .recommend-item__meta,
.dark .team-member__meta,
.dark .recommend-pagination__text,
.dark .snapshot-rule-list,
.dark .snapshot-skill-meta {
  color: #a9b3c1;
}

.dark .snapshot-members {
  border-top-color: #343b45;
}

.dark .snapshot-attr-card,
.dark .snapshot-skill-item,
.dark .snapshot-record-item {
  background: #252b33;
  border-color: #343b45;
}

.dark .snapshot-attr-value,
.dark .snapshot-skill-name,
.dark .snapshot-record-name,
.dark .snapshot-record-reward {
  color: #d7deed;
}

.dark .snapshot-record-scroll::-webkit-scrollbar-thumb,
.dark .snapshot-list::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.85);
}

.dark .snapshot-record-scroll::-webkit-scrollbar-track,
.dark .snapshot-list::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.85);
}

@media (max-width: 768px) {
  .team-deck__header,
  .team-card__header,
  .invite-item,
  .recommend-item {
    flex-direction: column;
  }

  .team-members {
    grid-template-columns: 1fr;
  }

  .recommend-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .snapshot-attr-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .snapshot-skill-list--slots {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>



// WEBPACK FOOTER //
// src/components/TeamDeck.vue